
import { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

import clientHelperService from '../services/clientHelperService';
import bedrockService from '../services/bedrockService';
import { formatUnixTimestampTime } from '../utils/dateUtils';

import FileUploadWizard from '../components/FileUploadWizard';
import FilePreviewModal from '../components/FilePreviewModal';
import DocumentGenerationJobsTable from '../components/DocumentGenerationJobsTable';
import DocumentTemplateConfigDialog from '../components/DocumentTemplateConfigDialog';
import SearchableDropdown from '../components/SearchableDropdown';

/**
 * Preprocess markdown text to ensure proper formatting.
 * - Ensures markdown headers (##, ###, etc.) have a blank line before them
 * - Ensures bold text that looks like headers (**ALL CAPS**) has proper spacing
 */
const preprocessMarkdown = (text) => {
  if (!text) return '';

  // Add newline before markdown headers (##, ###, etc.) if not already present
  let result = text.replace(/([^\n])(#{1,6}\s)/g, '$1\n\n$2');

  // Add newline before bold text that appears to be a header (all caps, at least 3 chars)
  // This handles patterns like "text. **HEADER**" -> "text.\n\n**HEADER**"
  result = result.replace(/([.!?:])(\s*)(\*\*[A-Z][A-Z\s]{2,}\*\*)/g, '$1\n\n$3');

  return result;
};

function SourcesAndFiltersSection({ sources, appliedFilters, fetchFileContent, sharepointFiles, onApplyFilters, docTypesData }) {
  const [sourcesCollapsed, setSourcesCollapsed] = useState(true);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFileUri, setModalFileUri] = useState(null);
  const [modalFilename, setModalFilename] = useState('');

  // Helper to convert doc_type ID to name
  const getDocTypeName = (docTypeId) => {
    if (!docTypeId || !docTypesData || docTypesData.length === 0) return docTypeId;
    const dt = docTypesData.find(d => d.id === docTypeId);
    return dt?.name || docTypeId;
  };

  // Helper to convert sub_type ID to name
  const getSubTypeName = (subTypeId) => {
    if (!subTypeId || !docTypesData || docTypesData.length === 0) return subTypeId;
    for (const dt of docTypesData) {
      if (dt.sub_types && dt.sub_types.length > 0) {
        const st = dt.sub_types.find(s => (typeof s === 'object' ? s.id : null) === subTypeId);
        if (st) return typeof st === 'object' ? st.name : st;
      }
    }
    return subTypeId;
  };

  const hasSources = sources && sources.length > 0;
  const hasFilters = appliedFilters && Object.keys(appliedFilters).length > 0;

  if (!hasSources && !hasFilters) {
    return null;
  }

  // Helper functions for sources
  const getFilenameFromUri = (uri) => {
    if (!uri) return '';
    const parts = uri.split('/');
    return parts[parts.length - 1];
  };

  const handleFileClick = (uri) => {
    setModalFileUri(uri);
    setModalFilename(getFilenameFromUri(uri));
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalFileUri(null);
    setModalFilename('');
  };

  const dedupeReferences = (references) => {
    const refSeen = new Set();
    return references.filter(ref => {
      const s3uri = ref.metadata?.['x-amz-bedrock-kb-source-uri'] || ref.location?.s3Location?.uri || null;
      const text = ref.content?.text || null;
      const refKey = s3uri || text || JSON.stringify(ref);
      if (refSeen.has(refKey)) return false;
      refSeen.add(refKey);
      return true;
    });
  };

  // Deduplicate sources
  const seen = new Set();
  const uniqueSources = hasSources ? sources.filter((src) => {
    let key;
    if (src.generatedResponsePart && Array.isArray(src.retrievedReferences)) {
      key = JSON.stringify({
        generatedResponsePart: src.generatedResponsePart,
        retrievedReferences: src.retrievedReferences.map(ref => {
          return {
            uri: ref.metadata?.['x-amz-bedrock-kb-source-uri'] || ref.location?.s3Location?.uri || null
          };
        })
      });
    } else {
      key = src.url || src.title || JSON.stringify(src);
    }
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }) : [];

  return (
    <div className="mt-2">
      {/* Buttons row */}
      <div className="d-flex gap-2 mb-2">
        {hasSources && (
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => setSourcesCollapsed(!sourcesCollapsed)}
            aria-expanded={!sourcesCollapsed}
          >
            {sourcesCollapsed ? 'Show Sources' : 'Hide Sources'}
          </button>
        )}
        {hasFilters && (
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => setFiltersCollapsed(!filtersCollapsed)}
            aria-expanded={!filtersCollapsed}
          >
            {filtersCollapsed ? 'Show Filters' : 'Hide Filters'}
          </button>
        )}
      </div>

      {/* Sources content */}
      {hasSources && !sourcesCollapsed && (
        <div className="mb-2">
          <ul className="mb-0 ps-3">
            {uniqueSources.map((src, i) => {
              // Handle sources with retrievedReferences (with or without generatedResponsePart)
              if (Array.isArray(src.retrievedReferences)) {
                let partText = src.generatedResponsePart;
                if (partText) {
                  if (typeof partText === 'object' && partText.textResponsePart && partText.textResponsePart.text) {
                    partText = partText.textResponsePart.text;
                  } else if (typeof partText === 'object') {
                    partText = JSON.stringify(partText);
                  }
                }
                const dedupedRefs = dedupeReferences(src.retrievedReferences);

                // If we have partText, wrap in an outer li, otherwise just return the references
                if (partText) {
                  return (
                    <li key={i}>
                      <div>
                        <div className="markdown-content"><ReactMarkdown remarkPlugins={[remarkBreaks]}>{preprocessMarkdown(partText)}</ReactMarkdown></div>
                        <ul>
                          {dedupedRefs.map((ref, j) => {
                            let s3uri = null;
                            if (ref.metadata && ref.metadata['x-amz-bedrock-kb-source-uri']) {
                              s3uri = ref.metadata['x-amz-bedrock-kb-source-uri'];
                            } else if (ref.location && ref.location.s3Location && ref.location.s3Location.uri) {
                              s3uri = ref.location.s3Location.uri;
                            }
                            if (s3uri) {
                              const filename = getFilenameFromUri(s3uri);
                              return (
                                <li key={j}>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                    onClick={() => handleFileClick(s3uri)}
                                  >
                                    {filename}
                                  </button>
                                </li>
                              );
                            }
                            return (
                              <li key={j}>
                                {ref.content && ref.content.text ? ref.content.text.substring(0, 80) + (ref.content.text.length > 80 ? '...' : '') : JSON.stringify(ref)}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </li>
                  );
                } else {
                  // No partText, just return the references directly
                  return dedupedRefs.map((ref, j) => {
                    let s3uri = null;
                    if (ref.metadata && ref.metadata['x-amz-bedrock-kb-source-uri']) {
                      s3uri = ref.metadata['x-amz-bedrock-kb-source-uri'];
                    } else if (ref.location && ref.location.s3Location && ref.location.s3Location.uri) {
                      s3uri = ref.location.s3Location.uri;
                    }
                    if (s3uri) {
                      const filename = getFilenameFromUri(s3uri);
                      return (
                        <li key={`${i}-${j}`}>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => handleFileClick(s3uri)}
                          >
                            {filename}
                          </button>
                        </li>
                      );
                    }
                    return (
                      <li key={`${i}-${j}`}>
                        {ref.content && ref.content.text ? ref.content.text.substring(0, 80) + (ref.content.text.length > 80 ? '...' : '') : JSON.stringify(ref)}
                      </li>
                    );
                  });
                }
              }
              if (src.url) {
                const filename = getFilenameFromUri(src.url);
                return (
                  <li key={i}>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={() => handleFileClick(src.url)}
                    >
                      {src.title || filename}
                    </button>
                  </li>
                );
              }
              return (
                <li key={i}>
                  {src.title || JSON.stringify(src)}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Filters content */}
      {hasFilters && !filtersCollapsed && (
        <div className="card bg-light">
          <div className="card-body py-2 px-3">
            {appliedFilters.dates && (
              <div className="mb-2">
                <strong>Date Range:</strong>
                <ul className="mb-0 ps-3">
                  {appliedFilters.dates.from && (
                    <li>From: {appliedFilters.dates.from}</li>
                  )}
                  {appliedFilters.dates.to && (
                    <li>To: {appliedFilters.dates.to}</li>
                  )}
                </ul>
              </div>
            )}
            {appliedFilters.doc_types && appliedFilters.doc_types.length > 0 && (
              <div className="mb-2">
                <strong>Doc Types:</strong>
                <div className="mt-1">
                  {appliedFilters.doc_types.map((doc_type, idx) => (
                    <span key={idx} className="badge bg-primary me-1">
                      {getDocTypeName(doc_type)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {appliedFilters.sub_types && appliedFilters.sub_types.length > 0 && (
              <div className="mb-2">
                <strong>Sub Types:</strong>
                <div className="mt-1">
                  {appliedFilters.sub_types.map((sub_type, idx) => (
                    <span key={idx} className="badge bg-primary me-1">
                      {getSubTypeName(sub_type)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {appliedFilters.file_ids && appliedFilters.file_ids.length > 0 && (
              <div className="mb-2">
                <strong>Selected Files:</strong>
                <div className="mt-1">
                  {appliedFilters.file_ids.map((fileId, idx) => {
                    // Look up filename from sharepointFiles using file_id
                    const file = sharepointFiles?.find(f => f.file_id === fileId);
                    const displayName = file?.name || fileId;
                    return (
                      <span key={idx} className="badge bg-info me-1">
                        {displayName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Apply Filters button */}
            {onApplyFilters && (
              <div className="mt-3 pt-2 border-top">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => onApplyFilters(appliedFilters)}
                >
                  <i className="bi bi-funnel-fill me-1"></i>
                  Apply to Manual Filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* File preview modal */}
      <FilePreviewModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        fileUri={modalFileUri}
        filename={modalFilename}
        fetchFileContent={fetchFileContent}
      />
    </div>
  );
}

// The eMoney data (values from the eMoney Build tool, checked by the FP team)
// answers alongside the documents. The choice is kept per browser.
const EMONEY_PREF_KEY = 'clientHelper.includeEmoneyData';
const EMONEY_HISTORY_TURNS = 3;
const SORRY_ANSWER = 'Sorry, I am unable to assist you with this request.';
// Skips worth telling the user about; a question that isn't about stored
// values skips the eMoney data routinely.
const NOTEWORTHY_EMONEY_SKIPS = ['filters', 'no_data', 'unavailable', 'scope'];

const readEmoneyPref = () => {
  try {
    return window.localStorage.getItem(EMONEY_PREF_KEY) !== 'off';
  } catch (e) {
    return true;
  }
};

const writeEmoneyPref = (on) => {
  try {
    window.localStorage.setItem(EMONEY_PREF_KEY, on ? 'on' : 'off');
  } catch (e) {
    // Private windows and blocked storage: the toggle still works for this visit.
  }
};

/**
 * How the eMoney data took part in an answer: a chip that opens the
 * documents behind its figures (and, for admins, the query and rows the
 * server only sends them), or a short note when it was left out for a reason
 * worth knowing.
 */
function EmoneyDataNote({ emoney }) {
  const [open, setOpen] = useState(false);
  if (!emoney) return null;
  if (!emoney.used) {
    if (!emoney.skipped_reason || !NOTEWORTHY_EMONEY_SKIPS.includes(emoney.skipped)) return null;
    return (
      <div className="small text-muted mt-2" data-testid="emoney-skipped">
        <i className="bi bi-info-circle me-1"></i>{emoney.skipped_reason}
      </div>
    );
  }
  const documents = emoney.documents || [];
  const sql = emoney.sql || [];
  const rows = emoney.rows || [];
  const columns = rows.length ? Object.keys(rows[0]) : [];
  return (
    <div className="mt-2" data-testid="emoney-used">
      <button type="button" className="btn btn-sm btn-outline-success" onClick={() => setOpen(!open)} aria-expanded={open}>
        <i className="bi bi-table me-1"></i>eMoney data
      </button>
      {open && (
        <div className="small mt-2 border rounded p-2 bg-white" data-testid="emoney-details">
          <div className="text-muted mb-1">
            Figures come from the eMoney data: values extracted from the client&apos;s statements and checked by the FP team.
          </div>
          {documents.length > 0 && (
            <ul className="mb-1 ps-3">
              {documents.map((name) => <li key={name}>{name}</li>)}
            </ul>
          )}
          {sql.length > 0 && (
            <details className="mt-1" data-testid="emoney-raw">
              <summary>Query and results</summary>
              {sql.map((query) => <pre key={query} className="small mb-1" style={{ whiteSpace: 'pre-wrap' }}>{query}</pre>)}
              {rows.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i}>{columns.map((c) => <td key={c}>{row[c]}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </details>
          )}
        </div>
      )}
    </div>
  );
}

const ClientHelper = () => {
  const [selectedClient, setSelectedClient] = useState('');
  const [includeEmoneyData, setIncludeEmoneyData] = useState(readEmoneyPref);
  const [question, setQuestion] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterMode, setFilterMode] = useState('none'); // 'none', 'auto', or 'manual'
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [sharepointFiles, setSharepointFiles] = useState([]); // {name, sp_path, kb_s3_path, file_id}
  const [selectedSharepointFiles, setSelectedSharepointFiles] = useState([]); // array of file_ids
  const [sharepointFileSearch, setSharepointFileSearch] = useState(''); // Search filter for SharePoint files
  const [docTypesData, setDocTypesData] = useState([]); // Full doc type objects with embedded sub_types and IDs
  const [selectedDocTypes, setSelectedDocTypes] = useState([]); // Selected doc_type names for UI
  const [selectedSubTypes, setSelectedSubTypes] = useState([]); // Selected sub_type names for UI
  const [activeTab, setActiveTab] = useState('ask-question'); // 'ask-question' or 'generate-document'
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [documentName, setDocumentName] = useState(''); // Human-friendly name for the document
  const [jobsControls, setJobsControls] = useState(null);
  const [showTemplateConfigDialog, setShowTemplateConfigDialog] = useState(false);

  // Derive lists of names for display from docTypesData
  const docTypesList = docTypesData.map(dt => dt.name).filter(Boolean);
  const subTypesList = [...new Set(docTypesData.flatMap(dt => (dt.sub_types || []).map(st => typeof st === 'object' ? st.name : st)))].filter(Boolean);

  // Helper to get filtered sub_types based on selected doc_type
  const getFilteredSubTypes = (selectedDocTypeName) => {
    if (!selectedDocTypeName || !docTypesData || docTypesData.length === 0) {
      return subTypesList;
    }
    const docTypeObj = docTypesData.find(dt => dt.name === selectedDocTypeName);
    if (!docTypeObj || !docTypeObj.sub_types || docTypeObj.sub_types.length === 0) {
      return subTypesList;
    }
    return docTypeObj.sub_types.map(st => typeof st === 'object' ? st.name : st).filter(Boolean);
  };

  // Helper to get IDs from selected names
  const getSelectedDocTypeIds = () => {
    return selectedDocTypes.map(name => {
      const dt = docTypesData.find(d => d.name === name);
      return dt ? dt.id : null;
    }).filter(Boolean);
  };

  const getSelectedSubTypeIds = () => {
    return selectedSubTypes.map(name => {
      for (const dt of docTypesData) {
        const st = (dt.sub_types || []).find(s => (typeof s === 'object' ? s.name : s) === name);
        if (st && typeof st === 'object' && st.id) return st.id;
      }
      return null;
    }).filter(Boolean);
  };

  // Compute available doc_types based on files and current sub_type/file selection
  // Only show doc_types that exist in files matching the selected sub_types and/or selected files
  const availableDocTypes = docTypesList.filter(docTypeName => {
    // Get the doc_type ID for this name
    const docTypeObj = docTypesData.find(dt => dt.name === docTypeName);
    if (!docTypeObj || !docTypeObj.id) return false;
    const docTypeId = docTypeObj.id;
    
    // Check if any sharepoint file has this doc_type_id
    const filesWithDocType = sharepointFiles.filter(f => f.doc_type_id === docTypeId);
    if (filesWithDocType.length === 0) return false;
    
    // If SharePoint files are selected, only show doc_types from those files
    if (selectedSharepointFiles.length > 0) {
      return filesWithDocType.some(f => selectedSharepointFiles.includes(f.file_id));
    }
    
    // If sub_types are selected, only show doc_types that have files with those sub_types
    if (selectedSubTypes.length > 0) {
      const selectedSubTypeIds = getSelectedSubTypeIds();
      return filesWithDocType.some(f => selectedSubTypeIds.includes(f.sub_type_id));
    }
    return true;
  });

  // Compute available sub_types based on files and current doc_type/file selection
  // Only show sub_types that exist in files matching the selected doc_types and/or selected files
  // Also filter by the doc_type hierarchy (only show sub_types that belong to selected doc_types)
  const availableSubTypes = useMemo(() => {
    // First, get the valid sub_types based on selected doc_types from the type definitions
    let validSubTypesFromDocTypes = subTypesList;
    if (selectedDocTypes.length > 0) {
      // Get sub_types from the selected doc_types
      const subTypesFromSelectedDocTypes = new Set();
      selectedDocTypes.forEach(docTypeName => {
        const filtered = getFilteredSubTypes(docTypeName);
        filtered.forEach(st => subTypesFromSelectedDocTypes.add(st));
      });
      validSubTypesFromDocTypes = [...subTypesFromSelectedDocTypes];
    }
    
    // Helper to get sub_type ID from name
    const getSubTypeIdFromName = (subTypeName) => {
      for (const dt of docTypesData) {
        const st = (dt.sub_types || []).find(s => (typeof s === 'object' ? s.name : s) === subTypeName);
        if (st && typeof st === 'object' && st.id) return st.id;
      }
      return null;
    };
    
    // Then filter by file availability
    return validSubTypesFromDocTypes.filter(subTypeName => {
      // Get the sub_type ID for this name
      const subTypeId = getSubTypeIdFromName(subTypeName);
      if (!subTypeId) return false;
      
      // Check if any sharepoint file has this sub_type_id
      const filesWithSubType = sharepointFiles.filter(f => f.sub_type_id === subTypeId);
      if (filesWithSubType.length === 0) return false;
      
      // If SharePoint files are selected, only show sub_types from those files
      if (selectedSharepointFiles.length > 0) {
        return filesWithSubType.some(f => selectedSharepointFiles.includes(f.file_id));
      }
      
      // If doc_types are selected, only show sub_types that have files with those doc_types
      if (selectedDocTypes.length > 0) {
        const selectedDocTypeIds = getSelectedDocTypeIds();
        return filesWithSubType.some(f => selectedDocTypeIds.includes(f.doc_type_id));
      }
      return true;
    });
  }, [subTypesList, selectedDocTypes, sharepointFiles, selectedSharepointFiles, docTypesData]);

  // Filter sharepoint files based on selected doc_types and sub_types
  const filteredSharepointFiles = sharepointFiles.filter(file => {
    if (!file.file_id || !file.kb_s3_path) return false;
    
    // If doc_types selected, file must match one of them (by ID)
    if (selectedDocTypes.length > 0) {
      const selectedDocTypeIds = getSelectedDocTypeIds();
      if (!selectedDocTypeIds.includes(file.doc_type_id)) {
        return false;
      }
    }
    
    // If sub_types selected, file must match one of them (by ID)
    if (selectedSubTypes.length > 0) {
      const selectedSubTypeIds = getSelectedSubTypeIds();
      if (!selectedSubTypeIds.includes(file.sub_type_id)) {
        return false;
      }
    }
    
    return true;
  });

  // Fetch clients from the client service API
  const didFetchClients = useRef(false);
  useEffect(() => {
    if (didFetchClients.current) return;
    didFetchClients.current = true;
    const fetchClients = async () => {
      const clientArray = await clientHelperService.fetchClients();
      // Sort clients alphabetically by client_name
      const sortedClients = clientArray.sort((a, b) =>
        a.client_name.localeCompare(b.client_name)
      );
      setClients(sortedClients);
      if (sortedClients.length > 0) {
        setSelectedClient(sortedClients[0].client_id);
      }
    };
    fetchClients();
  }, []);

  // Fetch document templates from the bedrock service
  const didFetchTemplates = useRef(false);
  useEffect(() => {
    if (didFetchTemplates.current) return;
    didFetchTemplates.current = true;
    const fetchTemplates = async () => {
      const response = await clientHelperService.getDocumentTemplates();
      if (response.success && response.templates) {
        setDocumentTemplates(response.templates);
      }
    };
    fetchTemplates();
  }, []);

  // Fetch doc types with full objects (includes IDs and sub_types)
  const didFetchMetadata = useRef(false);
  useEffect(() => {
    if (didFetchMetadata.current) return;
    didFetchMetadata.current = true;
    const fetchDocTypes = async () => {
      try {
        const response = await bedrockService.getDocTypes();
        const types = response?.doc_types || [];
        setDocTypesData(types);
      } catch (err) {
        console.warn('Failed to load doc types', err);
        setDocTypesData([]);
      }
    };
    fetchDocTypes();
  }, []);

  // Fetch SharePoint files when selectedClient changes
  useEffect(() => {
    if (!selectedClient) return;
    const fetchSharePointFiles = async () => {
      const response = await clientHelperService.getSharePointFiles(selectedClient);
      if (response.success && response.files) {
        setSharepointFiles(response.files);
      }
      // Reset selected files when client changes
      setSelectedSharepointFiles([]);
    };
    fetchSharePointFiles();
    // Clear chat history when client changes
    setChatHistory([]);
    setSessionId('');
  }, [selectedClient]);

  // Clear selected SharePoint files that are no longer in the filtered list when filters change
  useEffect(() => {
    if (selectedSharepointFiles.length === 0) return;
    const validFileIds = new Set(filteredSharepointFiles.map(f => f.file_id));
    const stillValidSelection = selectedSharepointFiles.filter(id => validFileIds.has(id));
    if (stillValidSelection.length !== selectedSharepointFiles.length) {
      setSelectedSharepointFiles(stillValidSelection);
    }
  }, [selectedDocTypes, selectedSubTypes, filteredSharepointFiles]);

  // Clear selected doc_types and sub_types that are no longer available when files are selected
  useEffect(() => {
    // Clear doc_types that are no longer available
    if (selectedDocTypes.length > 0) {
      const stillValidDocTypes = selectedDocTypes.filter(dt => availableDocTypes.includes(dt));
      if (stillValidDocTypes.length !== selectedDocTypes.length) {
        setSelectedDocTypes(stillValidDocTypes);
      }
    }
    // Clear sub_types that are no longer available
    if (selectedSubTypes.length > 0) {
      const stillValidSubTypes = selectedSubTypes.filter(st => availableSubTypes.includes(st));
      if (stillValidSubTypes.length !== selectedSubTypes.length) {
        setSelectedSubTypes(stillValidSubTypes);
      }
    }
  }, [selectedSharepointFiles, availableDocTypes, availableSubTypes]);

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    if (!selectedClient || !question.trim()) {
      alert('Please select a client and enter a question');
      return;
    }
    setLoading(true);
    try {
      // Build query payload based on filter mode
      const queryPayload = {
        client_id: selectedClient,
        question: question,
        session_id: sessionId || undefined,
        generate_filters: filterMode === 'auto',
        include_emoney_data: includeEmoneyData,
        // Recent turns with this client, so a follow-up can be understood by
        // the eMoney data (it can't share the documents' chat session).
        history: chatHistory
          .filter((entry) => entry.client_id === selectedClient && entry.answer && entry.answer !== SORRY_ANSWER)
          .slice(-EMONEY_HISTORY_TURNS)
          .map((entry) => ({ question: entry.question, answer: entry.answer })),
      };

      // Only send manual filters when in manual mode
      if (filterMode === 'manual') {
        if (fromDate) queryPayload.from_date = fromDate;
        if (toDate) queryPayload.to_date = toDate;
        if (selectedSharepointFiles.length > 0) queryPayload.file_ids = selectedSharepointFiles;
        const docTypeIds = getSelectedDocTypeIds();
        const subTypeIds = getSelectedSubTypeIds();
        if (docTypeIds.length > 0) queryPayload.doc_type_ids = docTypeIds;
        if (subTypeIds.length > 0) queryPayload.sub_type_ids = subTypeIds;
      }

      const data = await clientHelperService.queryClient(queryPayload);
      // Update chat history, include sources and applied_filters if present
      const newEntry = {
        question: question,
        answer: data.answer,
        client_id: selectedClient,
        sources: data.sources || [],
        applied_filters: data.applied_filters || null,
        emoney: data.emoney || null,
        prompt_history_id: data.prompt_history_id || null,
        timestamp: data.timestamp || null,
        feedback: null // { score, comment }
      };
      setChatHistory(prev => [...prev, newEntry]);
      setSessionId(data.session_id);
      setQuestion(''); // Clear the question field only
      // Do NOT clear fromDate or toDate
    } catch (error) {
      console.error('Error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDocument = async (e) => {
    e.preventDefault();
    if (!selectedClient || !selectedTemplate) {
      alert('Please select a client and a document template');
      return;
    }

    // Check if template has sections
    const template = documentTemplates.find(t => t.id === selectedTemplate);
    const hasSections = template && template.sections && template.sections.length > 0;

    // If template has no sections and manual mode with no SharePoint files selected, warn user
    if (!hasSections && filterMode === 'manual' && selectedSharepointFiles.length === 0) {
      alert('This template has no sections. Please select SharePoint files to include in the document.');
      return;
    }

    setLoading(true);
    try {
      // Build request payload based on filter mode
      const requestPayload = {
        client_id: selectedClient,
        template_id: selectedTemplate,
        document_name: documentName.trim() || null,
        filter_mode: filterMode
      };

      // Add manual filters if in manual mode
      if (filterMode === 'manual') {
        if (fromDate) requestPayload.from_date = fromDate;
        if (toDate) requestPayload.to_date = toDate;
        const docTypeIds = getSelectedDocTypeIds();
        const subTypeIds = getSelectedSubTypeIds();
        if (docTypeIds.length > 0) requestPayload.doc_type_ids = docTypeIds;
        if (subTypeIds.length > 0) requestPayload.sub_type_ids = subTypeIds;
      }

      // Always include file_ids if selected (needed for templates without sections)
      if (selectedSharepointFiles.length > 0) {
        requestPayload.file_ids = selectedSharepointFiles;
        requestPayload.kb_file_paths = selectedSharepointFiles.map(file_id => {
          const file = sharepointFiles.find(f => f.file_id === file_id);
          return file?.kb_s3_path || '';
        });
      }

      // Add output format parameters (default to PDF output in generated-documents folder)
      const clientName = selectedClientInfo?.client_name.replace(/[^a-zA-Z0-9_-]/g, '_') || selectedClient;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      // Use document name for output filename if provided, otherwise use template id
      const outputBaseName = documentName.trim() ? documentName.trim().replace(/[^a-zA-Z0-9_-]/g, '_') : selectedTemplate;
      requestPayload.output_bucket = 'hff-dossier';
      requestPayload.output_keys = [`generated-documents/${clientName}/${outputBaseName}_${timestamp}.pdf`,
      `generated-documents/${clientName}/${outputBaseName}_${timestamp}.docx`
      ];

      await clientHelperService.generateDocument(requestPayload);

      // Clear template selection and document name
      setSelectedTemplate('');
      setDocumentName('');
    } catch (error) {
      console.error('Error generating document:', error.message);
      alert(`Error generating document: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const [feedbackOpenIndex, setFeedbackOpenIndex] = useState(null);
  const [feedbackScore, setFeedbackScore] = useState(null); // 1 or -1
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [showUploadWizard, setShowUploadWizard] = useState(false);

  // Handle feedback submit
  const handleFeedbackSubmit = async (entryIdx) => {
    if (feedbackScore === null) return;
    const entry = chatHistory[entryIdx];
    if (!entry.prompt_history_id) return;
    setFeedbackSubmitting(true);
    try {
      await clientHelperService.submitPromptFeedback({
        prompt_history_id: entry.prompt_history_id,
        timestamp: entry.timestamp, // Pass sort key
        score: feedbackScore,
        comment: feedbackComment
      });
      setChatHistory(prev => prev.map((e, i) => i === entryIdx ? { ...e, feedback: { score: feedbackScore, comment: feedbackComment } } : e));
      setFeedbackOpenIndex(null);
      setFeedbackScore(null);
      setFeedbackComment('');
    } catch (err) {
      alert('Failed to submit feedback.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const clearChat = () => {
    setChatHistory([]);
    setSessionId('');
    setQuestion('');
    if (filterMode === 'manual') {
      setFromDate('');
      setToDate('');
      setSelectedSharepointFiles([]);
      setSelectedDocTypes([]);
      setSelectedSubTypes([]);
    }
  };

  // Fetch file content using clientHelperService
  const fetchFileContent = async (fileUri) => {
    // Helper to extract filename from s3 uri
    const getFilenameFromUri = (uri) => {
      if (!uri) return '';
      const parts = uri.split('/');
      return parts[parts.length - 1];
    };

    // Helper to extract client_id from s3 uri
    const getClientIdFromUri = (uri) => {
      if (!uri) return '';
      const parts = uri.split('/');
      return parts[parts.length - 2];
    };

    // Helper to extract doc_type from s3 uri
    const getDocTypeFromUri = (uri) => {
      if (!uri) return '';
      const parts = uri.split('/');
      return parts[parts.length - 3];
    };

    // fileUri is the S3 key or filename
    const filename = getFilenameFromUri(fileUri);
    const client_id = getClientIdFromUri(fileUri);
    const doc_type = getDocTypeFromUri(fileUri);

    // Check if it's a DOCX file and convert to PDF for preview
    const isDocx = filename.toLowerCase().endsWith('.docx');
    const convertToPdf = isDocx;

    const { blob, headers } = await clientHelperService.downloadFile(filename, client_id, doc_type, convertToPdf);
    const contentType = headers['content-type'] || '';

    console.log('File download result:', { filename, contentType, blobSize: blob.size, blob, convertToPdf });

    // Check if it's a text file by extension
    const textExtensions = ['.txt', '.csv', '.log', '.json', '.md', '.xml', '.html', '.css', '.js', '.py', '.java', '.cpp', '.h'];
    const isTextFile = textExtensions.some(ext => filename.toLowerCase().endsWith(ext));

    if (contentType.startsWith('image/')) {
      return URL.createObjectURL(blob);
    } else if (contentType.startsWith('text/') || contentType.includes('json') || isTextFile) {
      // Read blob as text
      return await blob.text();
    } else if (contentType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf') || convertToPdf) {
      // Create blob URL for PDF with explicit type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      console.log('Created PDF blob URL:', url);
      return url;
    } else if (contentType.includes('msword') || contentType.includes('officedocument')) {
      return URL.createObjectURL(blob);
    } else {
      // Fallback: download
      return URL.createObjectURL(blob);
    }
  };

  const selectedClientInfo = clients.find(c => c.client_id === selectedClient);

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3 mb-0">
              <i className="bi bi-robot me-2"></i>
              Client Helper - AI Assistant
            </h1>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowUploadWizard(true)}
              disabled={!selectedClient}
            >
              <i className="bi bi-cloud-upload me-2"></i>
              Upload Files
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Client Selection and Query Form */}
        <div className="col-lg-4 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-person-badge me-2"></i>
                Client Selection
              </h5>
            </div>
            <div className="card-body">
              {/* Client Selection - always visible */}
              <div className="mb-3">
                <label htmlFor="clientSelect" className="form-label">
                  Select Client
                </label>
                <SearchableDropdown
                  options={clients.map(c => c.client_name)}
                  value={selectedClientInfo?.client_name || ''}
                  onChange={(clientName) => {
                    const client = clients.find(c => c.client_name === clientName);
                    setSelectedClient(client ? client.client_id : '');
                  }}
                  placeholder="Choose a client..."
                  searchPlaceholder="Search clients..."
                  disabled={loading}
                />
                {selectedClientInfo && (
                  <div className="form-text text-muted">
                    {selectedClientInfo.description}
                  </div>
                )}
              </div>

              {/* Tab Navigation */}
              <ul className="nav nav-tabs mb-3" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'ask-question' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ask-question')}
                    type="button"
                    role="tab"
                  >
                    <i className="bi bi-question-circle me-1"></i>
                    Ask Question
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'generate-document' ? 'active' : ''}`}
                    onClick={() => setActiveTab('generate-document')}
                    type="button"
                    role="tab"
                  >
                    <i className="bi bi-file-earmark-text me-1"></i>
                    Generate Document
                  </button>
                </li>
              </ul>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Ask Question Tab */}
                {activeTab === 'ask-question' && (
                  <div className="tab-pane fade show active">
                    <form onSubmit={handleSubmitQuestion}>

                      <div className="mb-3">
                        <label htmlFor="questionInput" className="form-label">
                          Your Question
                        </label>
                        <textarea
                          id="questionInput"
                          className="form-control"
                          rows="4"
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          placeholder="Ask a question about the selected client..."
                          disabled={loading}
                        />
                      </div>

                      {/* Filter Mode Toggle */}
                      <div className="mb-3">
                        <label className="form-label">Filter Mode</label>
                        <div className="btn-group w-100" role="group" aria-label="Filter mode selection">
                          <input
                            type="radio"
                            className="btn-check"
                            name="filterModeAsk"
                            id="noFilterAsk"
                            checked={filterMode === 'none'}
                            onChange={() => {
                              setFilterMode('none');
                              setShowAdvanced(false);
                            }}
                          />
                          <label className="btn btn-outline-primary" htmlFor="noFilterAsk">
                            <i className="bi bi-x-circle me-1"></i>
                            No Filters
                          </label>

                          <input
                            type="radio"
                            className="btn-check"
                            name="filterModeAsk"
                            id="autoFilterAsk"
                            checked={filterMode === 'auto'}
                            onChange={() => {
                              setFilterMode('auto');
                              setShowAdvanced(false);
                              setFromDate('');
                              setToDate('');
                              setSelectedSharepointFiles([]);
                              setSelectedDocTypes([]);
                              setSelectedSubTypes([]);
                            }}
                          />
                          <label className="btn btn-outline-primary" htmlFor="autoFilterAsk">
                            <i className="bi bi-magic me-1"></i>
                            Auto Filter
                          </label>

                          <input
                            type="radio"
                            className="btn-check"
                            name="filterModeAsk"
                            id="manualFilterAsk"
                            checked={filterMode === 'manual'}
                            onChange={() => {
                              setFilterMode('manual');
                              setShowAdvanced(true);
                            }}
                          />
                          <label className="btn btn-outline-primary" htmlFor="manualFilterAsk">
                            <i className="bi bi-sliders me-1"></i>
                            Manual
                          </label>
                        </div>
                        {filterMode === 'none' && (
                          <div className="form-text text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            All the client's data will be searched.
                          </div>
                        )}
                        {filterMode === 'auto' && (
                          <div className="form-text text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            The AI will identify relevant document types, sub types, and date ranges to filter on based on your question.
                          </div>
                        )}
                      </div>

                      {/* Sources: the eMoney data alongside the documents */}
                      <div className="mb-3 form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          role="switch"
                          id="includeEmoneyData"
                          checked={includeEmoneyData}
                          onChange={(e) => {
                            setIncludeEmoneyData(e.target.checked);
                            writeEmoneyPref(e.target.checked);
                          }}
                          disabled={loading}
                        />
                        <label className="form-check-label" htmlFor="includeEmoneyData">
                          Include eMoney data
                        </label>
                        <div className="form-text text-muted">
                          Figures (balances, rates, premiums) come from the eMoney data, the values the FP team checked, whenever it has them.
                        </div>
                      </div>

                      {/* Advanced Filter - Only show in manual mode */}
                      {filterMode === 'manual' && (
                        <div className="mb-3">

                          {/* Date Range Filter */}
                          <div className="row mb-3">
                            <div className="col">
                              <label htmlFor="fromDateInput" className="form-label">From Date</label>
                              <input
                                type="date"
                                id="fromDateInput"
                                className="form-control"
                                value={fromDate}
                                onChange={e => setFromDate(e.target.value)}
                                disabled={loading}
                              />
                            </div>
                            <div className="col">
                              <label htmlFor="toDateInput" className="form-label">To Date</label>
                              <input
                                type="date"
                                id="toDateInput"
                                className="form-control"
                                value={toDate}
                                onChange={e => setToDate(e.target.value)}
                                disabled={loading}
                              />
                            </div>
                          </div>

                          {/* Document Type Filter */}
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label htmlFor="docTypeSelect" className="form-label">Document Types</label>
                              <select
                                id="docTypeSelect"
                                className="form-select"
                                multiple
                                value={selectedDocTypes}
                                onChange={e => {
                                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                                  setSelectedDocTypes(selected);
                                }}
                                disabled={loading}
                                style={{ minHeight: '100px' }}
                              >
                                {availableDocTypes.map(docType => (
                                  <option key={docType} value={docType}>
                                    {docType}
                                  </option>
                                ))}
                              </select>
                              {selectedDocTypes.length > 0 && (
                                <div className="mt-2">
                                  {selectedDocTypes.map(dt => (
                                    <span key={dt} className="badge bg-info me-1">
                                      {dt}
                                      <button
                                        type="button"
                                        className="btn-close btn-close-white ms-1"
                                        style={{ fontSize: '0.6rem' }}
                                        onClick={() => setSelectedDocTypes(prev => prev.filter(d => d !== dt))}
                                        disabled={loading}
                                        aria-label={`Remove ${dt}`}
                                      />
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6">
                              <label htmlFor="subTypeSelect" className="form-label">Sub-Types</label>
                              <select
                                id="subTypeSelect"
                                className="form-select"
                                multiple
                                value={selectedSubTypes}
                                onChange={e => {
                                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                                  setSelectedSubTypes(selected);
                                }}
                                disabled={loading}
                                style={{ minHeight: '100px' }}
                              >
                                {availableSubTypes.map(subType => (
                                  <option key={subType} value={subType}>
                                    {subType}
                                  </option>
                                ))}
                              </select>
                              {selectedSubTypes.length > 0 && (
                                <div className="mt-2">
                                  {selectedSubTypes.map(st => (
                                    <span key={st} className="badge bg-info me-1">
                                      {st}
                                      <button
                                        type="button"
                                        className="btn-close btn-close-white ms-1"
                                        style={{ fontSize: '0.6rem' }}
                                        onClick={() => setSelectedSubTypes(prev => prev.filter(s => s !== st))}
                                        disabled={loading}
                                        aria-label={`Remove ${st}`}
                                      />
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* SharePoint Files Filter */}
                          <div>
                            <label htmlFor="sharepointFileSelect" className="form-label">SharePoint Files</label>
                            <div className="input-group mb-2">
                              <span className="input-group-text">
                                <i className="bi bi-search"></i>
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search files..."
                                value={sharepointFileSearch}
                                onChange={(e) => setSharepointFileSearch(e.target.value)}
                                disabled={loading}
                              />
                              {sharepointFileSearch && (
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setSharepointFileSearch('')}
                                  disabled={loading}
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              )}
                            </div>
                            <select
                              id="sharepointFileSelect"
                              className="form-select"
                              multiple
                              value={selectedSharepointFiles}
                              onChange={e => {
                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                setSelectedSharepointFiles(selected);
                              }}
                              disabled={loading}
                              style={{ minHeight: '120px' }}
                            >
                              {filteredSharepointFiles
                                .filter(file =>
                                  !sharepointFileSearch ||
                                  file.name.toLowerCase().includes(sharepointFileSearch.toLowerCase())
                                )
                                .map(file => (
                                <option key={file.file_id} value={file.file_id}>
                                  {file.name}
                                </option>
                              ))}
                            </select>
                            <div className="form-text text-muted">
                              <i className="bi bi-info-circle me-1"></i>
                              Hold Ctrl (or Cmd) to select multiple files
                            </div>
                            {selectedSharepointFiles.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted">Selected: </small>
                                {selectedSharepointFiles.map(fileId => {
                                  // Find the file to get the display name
                                  const file = sharepointFiles.find(f => f.file_id === fileId);
                                  const fileName = file?.name || fileId;
                                  return (
                                    <span key={fileId} className="badge bg-secondary me-1">
                                      {fileName}
                                      <button
                                        type="button"
                                        className="btn-close btn-close-white ms-1"
                                        style={{ fontSize: '0.6rem' }}
                                        onClick={() => setSelectedSharepointFiles(prev => prev.filter(f => f !== fileId))}
                                        disabled={loading}
                                        aria-label={`Remove ${fileName}`}
                                      />
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="d-grid gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading || !selectedClient || !question.trim()}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-send me-2"></i>
                              Ask Question
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={clearChat}
                          disabled={loading}
                        >
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Clear Chat
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Generate Document Tab */}
                {activeTab === 'generate-document' && (
                  <div className="tab-pane fade show active">
                    <form onSubmit={handleGenerateDocument}>
                      <div className="mb-3">
                        <label htmlFor="templateSelect" className="form-label">
                          Select Document Template
                        </label>
                        <div className="input-group">
                          <select
                            id="templateSelect"
                            className="form-select"
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            disabled={loading}
                          >
                            <option value="">Choose a template...</option>
                            {documentTemplates.map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowTemplateConfigDialog(true)}
                            disabled={loading}
                            title="Configure Document Templates"
                          >
                            <i className="bi bi-gear"></i>
                          </button>
                        </div>
                        {selectedTemplate && (
                          <div className="form-text text-muted">
                            {documentTemplates.find(t => t.id === selectedTemplate)?.description}
                          </div>
                        )}
                      </div>

                      {/* Document Name Input - Show when template is selected */}
                      {selectedTemplate && (
                        <div className="mb-3">
                          <label htmlFor="documentName" className="form-label">
                            Document Name <span className="text-muted">(optional)</span>
                          </label>
                          <input
                            type="text"
                            id="documentName"
                            className="form-control"
                            value={documentName}
                            onChange={(e) => setDocumentName(e.target.value)}
                            placeholder="Enter a friendly name for this document..."
                            disabled={loading}
                          />
                          <div className="form-text text-muted">
                            This name will be used for the output file and shown in the jobs list
                          </div>
                        </div>
                      )}

                      {/* SharePoint Files - Only show for templates that require file selection (no sections) */}
                      {selectedTemplate && documentTemplates.find(t => t.id === selectedTemplate)?.sections?.length === 0 && (
                          <div className="mb-3">
                            <label htmlFor="sharepointFileSelectDoc" className="form-label">
                              <i className="bi bi-exclamation-circle-fill text-warning me-1"></i>
                              <span className="text-warning">Required: </span>
                              SharePoint Files
                            </label>
                            <div className="input-group mb-2">
                              <span className="input-group-text">
                                <i className="bi bi-search"></i>
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search files..."
                                value={sharepointFileSearch}
                                onChange={(e) => setSharepointFileSearch(e.target.value)}
                                disabled={loading}
                              />
                              {sharepointFileSearch && (
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setSharepointFileSearch('')}
                                  disabled={loading}
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              )}
                            </div>
                            <select
                              id="sharepointFileSelectDoc"
                              className="form-select"
                              multiple
                              value={selectedSharepointFiles}
                              onChange={e => {
                                const selected = Array.from(e.target.selectedOptions, option => option.value);
                                setSelectedSharepointFiles(selected);
                              }}
                              disabled={loading}
                              style={{ minHeight: '120px' }}
                            >
                              {filteredSharepointFiles
                                .filter(file =>
                                  !sharepointFileSearch ||
                                    file.name.toLowerCase().includes(sharepointFileSearch.toLowerCase())
                                )
                                .map(file => (
                                  <option key={file.file_id} value={file.file_id}>
                                    {file.name}
                                  </option>
                                ))}
                            </select>
                            <div className="form-text text-muted">
                              <i className="bi bi-info-circle me-1"></i>
                              Select the files to include in the document. Hold Ctrl (or Cmd) to select multiple files.
                            </div>
                            {selectedSharepointFiles.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted">Selected: </small>
                                {selectedSharepointFiles.map(fileId => {
                                  // Find the file to get the display name
                                  const file = sharepointFiles.find(f => f.file_id === fileId);
                                  const fileName = file?.name || fileId;
                                  return (
                                    <span key={fileId} className="badge bg-secondary me-1">
                                      {fileName}
                                      <button
                                        type="button"
                                        className="btn-close btn-close-white ms-1"
                                        style={{ fontSize: '0.6rem' }}
                                        onClick={() => setSelectedSharepointFiles(prev => prev.filter(f => f !== fileId))}
                                        disabled={loading}
                                        aria-label={`Remove ${fileName}`}
                                      />
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                      <div className="d-grid gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading || !selectedClient || !selectedTemplate}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Generating...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-file-earmark-text me-2"></i>
                              Generate Document
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={clearChat}
                          disabled={loading}
                        >
                          <i className="bi bi-arrow-clockwise me-2"></i>
                          Clear Chat
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Chat History and Response / Document Generation Jobs */}
        <div className="col-lg-8 d-flex flex-column" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="card flex-grow-1 d-flex flex-column" style={{ height: '100%' }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                {activeTab === 'ask-question' ? (
                  <>
                    <i className="bi bi-chat-dots me-2"></i>
                    Conversation
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Document Generation Jobs
                  </>
                )}
              </h5>
              {activeTab === 'generate-document' && jobsControls}
            </div>
            <div className="card-body chat-scroll-area flex-grow-1 d-flex flex-column" style={{ minHeight: 0, height: '100%', overflow: 'hidden' }}>
              {/* Ensure vertical scroll for overflow */}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>

                {/* Show Document Generation Jobs Table when on generate-document tab */}
                {activeTab === 'generate-document' ? (
                  <DocumentGenerationJobsTable
                    clientId={selectedClient}
                    clientHelperService={clientHelperService}
                    onControlsRender={setJobsControls}
                    documentTemplates={documentTemplates}
                  />
                ) : (
                  // Show conversation history when on ask-question tab
                  <>
                    {chatHistory.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <i className="bi bi-chat-square-dots display-4 mb-3"></i>
                        <p>No conversation yet. Select a client and ask a question to get started!</p>
                      </div>
                    ) : (
                      <div className="chat-container">
                        {chatHistory.map((entry, index) => (
                          <div key={index} className="mb-4">
                            {/* Question */}
                            <div className="d-flex justify-content-end mb-2">
                              <div className="card bg-primary text-white" style={{ maxWidth: '80%' }}>
                                <div className="card-body py-2 px-3">
                                  <small className="opacity-75">
                                    {clients.find(c => c.client_id === entry.client_id)?.client_name} -
                                    {entry.timestamp ? formatUnixTimestampTime(entry.timestamp) : ''}
                                  </small>
                                  <div>{entry.question}</div>
                                </div>
                              </div>
                            </div>

                            {/* Answer */}
                            <div className="d-flex justify-content-start">
                              <div className="card bg-light" style={{ maxWidth: '80%' }}>
                                <div className="card-body py-2 px-3">
                                  <small className="text-muted">
                                    <i className="bi bi-robot me-1"></i>
                                    AI Assistant
                                  </small>
                                  <div className="mt-1 markdown-content">
                                    <ReactMarkdown remarkPlugins={[remarkBreaks]}>{preprocessMarkdown(entry.answer)}</ReactMarkdown>
                                  </div>
                                  <EmoneyDataNote emoney={entry.emoney} />

                                  {/* Render sources and filters - buttons on same row, content below */}
                                  {(entry.sources && entry.sources.length > 0) || entry.applied_filters ? (
                                    <SourcesAndFiltersSection
                                      sources={entry.sources}
                                      appliedFilters={entry.applied_filters}
                                      fetchFileContent={fetchFileContent}
                                      sharepointFiles={sharepointFiles}
                                      docTypesData={docTypesData}
                                      onApplyFilters={(filters) => {
                                        // Switch to manual mode and populate filter values
                                        setFilterMode('manual');
                                        setShowAdvanced(true);
                                        // Apply date filters
                                        if (filters.dates) {
                                          setFromDate(filters.dates.from || '');
                                          setToDate(filters.dates.to || '');
                                        } else {
                                          setFromDate('');
                                          setToDate('');
                                        }
                                        // Apply doc_types - convert IDs to names for manual filter
                                        if (filters.doc_types && filters.doc_types.length > 0) {
                                          const docTypeNames = filters.doc_types.map(id => {
                                            const dt = docTypesData.find(d => d.id === id);
                                            return dt?.name || id;
                                          });
                                          setSelectedDocTypes(docTypeNames);
                                        } else {
                                          setSelectedDocTypes([]);
                                        }
                                        // Apply sub_types - convert IDs to names for manual filter
                                        if (filters.sub_types && filters.sub_types.length > 0) {
                                          const subTypeNames = filters.sub_types.map(id => {
                                            for (const dt of docTypesData) {
                                              const st = (dt.sub_types || []).find(s => (typeof s === 'object' ? s.id : null) === id);
                                              if (st) return typeof st === 'object' ? st.name : st;
                                            }
                                            return id;
                                          });
                                          setSelectedSubTypes(subTypeNames);
                                        } else {
                                          setSelectedSubTypes([]);
                                        }
                                        // Apply file_ids if present
                                        if (filters.file_ids && filters.file_ids.length > 0) {
                                          setSelectedSharepointFiles(filters.file_ids);
                                        } else {
                                          setSelectedSharepointFiles([]);
                                        }
                                      }}
                                    />
                                  ) : null}

                                  {/* Feedback widget: show for any entry with prompt_history_id and not yet rated, except for 'Sorry, I am unable to assist you with this request.' */}
                                  {entry.prompt_history_id && !entry.feedback && entry.answer !== 'Sorry, I am unable to assist you with this request.' && (
                                    <div className="mt-3">
                                      <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => {
                                          if (feedbackOpenIndex !== index) {
                                            setFeedbackScore(null);
                                            setFeedbackComment('');
                                          }
                                          setFeedbackOpenIndex(feedbackOpenIndex === index ? null : index);
                                        }}
                                        aria-expanded={feedbackOpenIndex === index}
                                      >
                                        {feedbackOpenIndex === index ? 'Hide Feedback' : 'Rate this response'}
                                      </button>
                                      {feedbackOpenIndex === index && (
                                        <div className="mt-2 border rounded p-2 bg-white shadow-sm">
                                          <div className="d-flex align-items-center mb-2">
                                            <span className="me-2">How was this response?</span>
                                            <button
                                              type="button"
                                              className={`btn btn-sm me-1 ${feedbackScore === 1 ? 'btn-success' : 'btn-outline-success'}`}
                                              onClick={() => setFeedbackScore(1)}
                                              disabled={feedbackSubmitting}
                                              aria-label="Thumbs up"
                                            >
                                              <i className="bi bi-hand-thumbs-up"></i>
                                            </button>
                                            <button
                                              type="button"
                                              className={`btn btn-sm ${feedbackScore === -1 ? 'btn-danger' : 'btn-outline-danger'}`}
                                              onClick={() => setFeedbackScore(-1)}
                                              disabled={feedbackSubmitting}
                                              aria-label="Thumbs down"
                                            >
                                              <i className="bi bi-hand-thumbs-down"></i>
                                            </button>
                                          </div>
                                          <textarea
                                            className="form-control mb-2"
                                            rows="2"
                                            placeholder="Optional comment..."
                                            value={feedbackComment}
                                            onChange={e => setFeedbackComment(e.target.value)}
                                            disabled={feedbackSubmitting}
                                          />
                                          <div className="d-flex justify-content-end">
                                            <button
                                              className="btn btn-primary btn-sm"
                                              onClick={() => handleFeedbackSubmit(index)}
                                              disabled={feedbackScore === null || feedbackSubmitting}
                                            >
                                              {feedbackSubmitting ? (
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                              ) : null}
                                              Submit
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {/* Failed KB jobs have no history id; hide leftover debug text */}
                                  {!entry.prompt_history_id &&
                                    !entry.feedback &&
                                    entry.answer !==
                                      'Sorry, I am unable to assist you with this request.' && (
                                    <div className="mt-3 text-danger small">
                                      (No prompt_history_id returned for this response)
                                    </div>
                                  )}
                                  {/* If feedback submitted, show a thank you */}
                                  {entry.feedback && (
                                    <div className="mt-3 text-success small">
                                      Thank you for your feedback!
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Current Response (if loading) */}
                    {loading && activeTab === 'ask-question' && (
                      <div className="d-flex justify-content-start">
                        <div className="card bg-light" style={{ maxWidth: '80%' }}>
                          <div className="card-body py-2 px-3">
                            <small className="text-muted">
                              <i className="bi bi-robot me-1"></i>
                              AI Assistant
                            </small>
                            <div className="mt-1">
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Thinking...
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Upload Wizard */}
      <FileUploadWizard
        isOpen={showUploadWizard}
        onClose={() => setShowUploadWizard(false)}
        selectedClient={selectedClient}
        selectedClientName={selectedClientInfo?.client_name || 'ERROR'}
      />

      {/* Document Template Configuration Dialog */}
      <DocumentTemplateConfigDialog
        isOpen={showTemplateConfigDialog}
        onClose={() => setShowTemplateConfigDialog(false)}
        onTemplatesUpdated={() => {
          // Refresh the templates list
          const fetchTemplates = async () => {
            const response = await clientHelperService.getDocumentTemplates();
            if (response.success && response.templates) {
              setDocumentTemplates(response.templates);
            }
          };
          fetchTemplates();
        }}
        clientHelperService={clientHelperService}
      />
    </div>
  );
};

export default ClientHelper;
